/**
 * One key interaction, as a thing you can open.
 *
 * The ARTIFACT fill of the one shell (`CardShell`, RFC 0300 §4g). A run of
 * tool calls is drawn in the same shell by `ToolCallCard`, so the thread has
 * one visual system; what makes an artifact card different is that its label
 * is the thing's CURRENT name and opens the thing, not that it has a border
 * the others lack.
 *
 * IT LEADS WITH THE VERB, not with the tool. "Wrote  Release plan" is what
 * happened; `create_document {"title":"Release plan"}` is how it was typed. The
 * tool name is available under the disclosure, where somebody debugging can
 * find it and nobody else has to read it.
 *
 * A card with nothing to open is still a card. It does not become a link it
 * cannot honour: a document whose id never came back, or one that was deleted,
 * renders flat and unclickable rather than opening an empty pane.
 */

import type { ReactNode } from 'react'
import { SessionMark } from '../../services/sessions/SessionMark'
import { CardShell } from './CardShell'
import { type SubthreadState, subthreadLabel } from './subthreadState'
import { ToolCallBlock, type ToolResult } from './ToolCallBlock'
import type { ThreadCard as Card, CardTarget } from './threadCards'
import type { ToolCall } from './threadRows'

/**
 * The state of the subthread under this post (RFC 0302 S5).
 *
 * In place, on the card that is already there, rather than as a new row when
 * the branch finishes: the card IS the branch, and a conversation that grew a
 * line every time a child changed state would be a status feed.
 *
 * Only "waiting for you" is emphasised. It is the one state where nothing moves
 * until the reader does something, and if every state shouted, none would.
 */
export function SubthreadChip({ state }: { state: SubthreadState }) {
  return (
    <span
      className={
        state === 'waiting'
          ? 'rounded-md bg-muted px-1.5 py-0.5 text-[0.72rem] text-foreground'
          : 'text-[0.72rem] text-muted-foreground'
      }
    >
      {subthreadLabel(state)}
    </span>
  )
}

/**
 * One card per thing the agent touched: a document, a session, a comment.
 *
 * No glyph per kind (see `CardShell`): the verb says what happened. A session
 * card carries that session's own mark (RFC 0311), which is identity rather
 * than a kind icon, so it tells you WHICH session, not just that it was one.
 */
export function ThreadCard({
  card,
  call,
  result,
  onOpenDocument,
  onOpenSession,
  resolveTitle,
  resolveSessionState,
  times = 1,
  renderUndo,
  body,
  trailing,
}: {
  card: Card
  /** Content the card always shows under its header, with no chevron: a stop
   *  report's preview (RFC 0348 3e). Replaces the call disclosure. */
  body?: ReactNode
  /** A state at the right of the header, in place of the subthread chip. */
  trailing?: ReactNode
  /** How many consecutive calls did this same thing to this same target. Shown
   *  as "×N" beside the label, so eleven edits to one document are one row. */
  times?: number
  /** The call behind the card, when one produced it. Kept inspectable under the
   *  disclosure; an event card has none and simply does not offer it. */
  call?: ToolCall
  result?: ToolResult
  onOpenDocument?: (documentId: string) => void
  onOpenSession?: (sessionId: string) => void
  /**
   * The CURRENT title of the thing, from the project registry.
   *
   * Preferred over the label the call was made with, because a document renamed
   * after it was written should read by its name now, not by the name it had in
   * a message three days ago. Returns undefined when the id is unknown here —
   * another project's session, or one since deleted — and the card falls back to
   * what the call said, which is the last true thing anybody recorded.
   */
  resolveTitle?: (target: CardTarget, id: string) => string | undefined
  /**
   * RFC 0302 S5: what the subthread under this post is doing, from the project
   * registry. Undefined when nothing has reported, and the card then says
   * nothing rather than guessing.
   */
  resolveSessionState?: (sessionId: string) => SubthreadState | undefined
  /** RFC 0301 S9: the undo for an agent edit's change, beside the label. */
  renderUndo?: (documentId: string, hash: string) => ReactNode
}) {
  const id = card.target === 'session' ? card.sessionId : card.documentId
  const resolved = id ? resolveTitle?.(card.target, id) : undefined
  const state =
    card.target === 'session' && id ? resolveSessionState?.(id) : undefined
  // A comment card's label is a quotation of what it is about, never a title, so
  // it is left alone even when the document it sits on is known.
  const label =
    card.target === 'comment' ? card.label : (resolved ?? card.label)

  const openIt =
    card.pending || !id
      ? undefined
      : card.target === 'session'
        ? onOpenSession && (() => onOpenSession(id))
        : onOpenDocument && (() => onOpenDocument(id))

  return (
    <CardShell
      verb={card.verb}
      label={label}
      // The session's own mark, the same one its row draws (RFC 0311), so the
      // card and the list are recognisably the same session.
      {...(card.target === 'session' && id
        ? { mark: <SessionMark id={id} size={20} /> }
        : {})}
      {...(openIt ? { onOpen: openIt } : {})}
      times={times}
      {...(card.detail ? { detail: card.detail } : {})}
      // A session card pulses while the CHILD works, not while the call is out:
      // `start_session` returns the moment the child is opened, and the thing
      // still running after that is the subthread.
      pending={state === 'working' || Boolean(card.pending)}
      {...(trailing
        ? { trailing }
        : state
          ? { trailing: <SubthreadChip state={state} /> }
          : card.documentId && card.changeHash && renderUndo
            ? { trailing: renderUndo(card.documentId, card.changeHash) }
            : {})}
      {...(body
        ? { body, always: true }
        : call
          ? {
              // The call behind the card stays inspectable: a card is a better
              // view of it, not a cover over its arguments.
              body: (
                <ToolCallBlock
                  name={call.name}
                  args={call.arguments}
                  result={result}
                />
              ),
            }
          : {})}
    />
  )
}
